import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner, CloseButton } from "react-bootstrap";
import Alert from "../common/Alert";
import RichTextEditor from "../fieldDetails/RichTextEditor";
import SessionHelper from "../../helpers/SessionHelper";
import {
  EMPTY_FARMER_NOTE,
  ERROR_FETCHING_FARMER_NOTES,
  ERROR_SAVING_FARMER_NOTES,
  FARMER_NOTES_SAVED_SUCCESSFULLY,
} from "../../configurations/MessageConstants";
import { ThreeDots } from "react-loader-spinner";
import SuperUserService from "../../api-service/SuperUserService";
import DashboardHelper from "../../helpers/DashboardHelper";
import { useSelector } from "react-redux";
import StorageHelper from "../../helpers/StorageHelper";
import {
  DANGER,
  EMPTY,
  INFO,
  SHOW_STAFF_NOTES,
} from "../../configurations/Constants";
import { StatusCodes } from "../../configurations/status-codes";

function StaffUserNotes() {
  const loggedInUser = useSelector((state) => state.user.loggedInUser);
  const [showStaffUserNotes, setShowStaffUserNotes] = useState(
    SessionHelper.isStaffMember() &&
      StorageHelper.getItem(SHOW_STAFF_NOTES) === "true"
  );
  const [userNotes, setUserNotes] = useState(EMPTY);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [alertMessage, setAlertMessage] = useState(EMPTY);
  const [variant, setVariant] = useState(EMPTY);
  const [showAlert, setShowAlert] = useState(false);
  const [sendBtnClick, setSendBtnClick] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    getUserNotes();
    StorageHelper.setItem(SHOW_STAFF_NOTES, false);
  }, []);

  const getUserNotes = async () => {
    try {
      const { data } = await SuperUserService.getStaffNotesForUser();
      setUserNotes(data.data.note);
      setLoadingNotes(false);
    } catch (error) {
      console.error(ERROR_FETCHING_FARMER_NOTES, error);
      if (error.response.status === StatusCodes.BAD_REQUEST) {
        setLoadingNotes(false);
        setAlertMessage(ERROR_FETCHING_FARMER_NOTES);
        setVariant(DANGER);
        setShowAlert(true);
      }
    }
  };

  const handleClose = () => {
    setShowStaffUserNotes(false);
  };

  const handleCloseAlert = () => {
    setAlertMessage(EMPTY);
    setVariant(EMPTY);
    setShowAlert(false);
  };

  const handleSubmitNotes = async () => {
    setSendBtnClick(true);
    await SessionHelper.handleRefreshToken();
    try {
      let finalValue = userNotes;
      let finalValueWithLineBreaks = finalValue.replaceAll(/<br>/gi, "\n");
      let finalValueWithoutHtml = DashboardHelper.stripHtml(
        finalValueWithLineBreaks
      );
      if (finalValueWithoutHtml.trim().length === 0) {
        setAlertMessage(EMPTY_FARMER_NOTE);
        setVariant(DANGER);
        setShowAlert(true);
        setSendBtnClick(false);
        return;
      }
      let payload = {
        note: finalValueWithoutHtml,
        note_html: `<div style="font-family:arial,sans-serif; font-size:13px; color:gray;">${finalValue}</div>`,
      };
      await SuperUserService.updateUserStaffNotes(payload);
      setAlertMessage(FARMER_NOTES_SAVED_SUCCESSFULLY);
      setVariant(INFO);
      setShowAlert(true);
      setSendBtnClick(false);
    } catch (error) {
      setAlertMessage(ERROR_SAVING_FARMER_NOTES);
      setVariant(DANGER);
      setShowAlert(true);
      setSendBtnClick(false);
    }
  };

  return (
    <Modal
      show={showStaffUserNotes}
      onHide={handleClose}
      className="staff-user-notes-modal"
    >
      <Modal.Header>
        <Modal.Title>
          <h3>Farmer Notes ({loggedInUser})</h3>
        </Modal.Title>
        <CloseButton
          className="close field-details-close"
          onClick={(event) => handleClose(event)}
        />
      </Modal.Header>
      <div className="staff-user-notes p-3">
        {loadingNotes ? (
          <div className="d-flex justify-content-center">
            <ThreeDots color="#00BFFF" height={80} width={80} />
          </div>
        ) : (
          <div>
            {showAlert && (
              <Alert
                key="messageAlert"
                variant={variant}
                closeAlert={(e) => handleCloseAlert(e)}
                alertMessage={alertMessage}
                dismissible
              />
            )}
            {!isEditing ? (
              <div className="d-flex justify-content-center align-items-center my-3">
                <Button
                  variant="link"
                  className="p-0 d-flex align-items-center gap-2 text-decoration-none"
                  onClick={() => setIsEditing(true)}
                  aria-label="Edit notes"
                >
                  <h4 className="mb-0 text-primary fw-semibold">
                    View or Edit Farmer Notes
                  </h4>
                  <i className="fa fa-pencil " />
                </Button>
              </div>
            ) : (
              <>
                <h5 className="staff-user-notes-label my-1">
                  Enter your notes here:
                </h5>
                <RichTextEditor
                  displayString={userNotes}
                  setDisplayString={setUserNotes}
                />
                <h6 className="mt-4">
                  NOTE: These notes are available only for the Twin Platte NRD
                  authorized personnel to view
                </h6>
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button
                    variant="primary"
                    type="submit"
                    className="staff-user-notes-close"
                    onClick={(e) => handleClose(e)}
                  >
                    Close
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    onClick={handleSubmitNotes}
                    className="staff-user-notes-send"
                    disabled={sendBtnClick ? true : false}
                  >
                    {sendBtnClick === true ? (
                      <div>
                        <Spinner
                          as="span"
                          animation="grow"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />{" "}
                        <span>{"Saving..."}</span>
                      </div>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default StaffUserNotes;
